import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { IDripCampaignStepSpec, Template } from '@feathr/blackbox';
import { Button, ButtonValid, CardV2 as Card, Label } from '@feathr/components';
import { flattenErrors } from '@feathr/hooks';

import MergeDataInputs from '../../../EmailDetails/MergeDataInputs';
import { validateAutomation } from '../../DripCampaignEdit.utils';
import EmailInputs from '../EmailInputs';
import useEphemeralCampaign from '../useEphemeralCampaign';
import StepTargeting from './StepTargeting';

interface IEditAutomationProps {
  dripCampaignId: string;
  step: IDripCampaignStepSpec;
  onApply: (updatedStep: IDripCampaignStepSpec) => Promise<void>;
  onSync: (updatedStep: IDripCampaignStepSpec, template: Template | null) => Promise<void>;
  onCancel: () => void;
}

function EditAutomation({
  dripCampaignId,
  step,
  onApply,
  onSync,
  onCancel,
}: Readonly<IEditAutomationProps>): JSX.Element {
  const { t } = useTranslation();
  const { campaign, handleApply, handleSync } = useEphemeralCampaign({
    dripCampaignId,
    onApply,
    onSync,
    step,
  });
  const validationErrors = validateAutomation(campaign);

  useEffect(() => {
    handleSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.isDirty]);

  return (
    <Card theme={'disabled'} width={'full'}>
      <Card.Content>
        <Label weight={'semibold'}>{t('Automation')}</Label>
        <StepTargeting campaign={campaign} validationErrors={validationErrors} />
      </Card.Content>
      <Card.Content>
        <Label weight={'semibold'}>{t('Email')}</Label>
        <EmailInputs campaign={campaign} validationErrors={validationErrors} />
      </Card.Content>
      <Card.Content>
        <Label margin={'condensed'} weight={'semibold'}>
          {t('Default Merge Data')}
        </Label>
        <p>
          {t(
            'These values will be used to replace merge tags when the recipient does not have data for the corresponding field.',
          )}
        </p>
        <MergeDataInputs campaign={campaign} disabled={false} />
      </Card.Content>
      <Card.Actions>
        <Button onClick={onCancel}>{t('Cancel')}</Button>
        <ButtonValid
          errors={flattenErrors(validationErrors)}
          onClick={handleApply}
          type={'primary'}
        >
          {t('Apply')}
        </ButtonValid>
      </Card.Actions>
    </Card>
  );
}

export default observer(EditAutomation);
